import { useCallback, useMemo, useState } from 'react'

/**
 * 批量选择 Hook
 * 用于列表的多选功能
 * 
 * @example
 * ```tsx
 * const {
 *   selectedIds,
 *   isSelected,
 *   toggle,
 *   selectAll,
 *   clearSelection,
 *   isAllSelected,
 *   isPartiallySelected,
 *   selectedCount,
 * } = useBatchSelection(items, (item) => item.id)
 * 
 * <input
 *   type="checkbox"
 *   checked={isAllSelected}
 *   indeterminate={isPartiallySelected}
 *   onChange={(e) => e.target.checked ? selectAll() : clearSelection()}
 * />
 * 
 * {items.map((item) => (
 *   <input
 *     type="checkbox"
 *     checked={isSelected(item.id)}
 *     onChange={() => toggle(item.id)}
 *   />
 * ))}
 * ```
 */
export function useBatchSelection<T, K extends string | number = string>(
  items: T[],
  getKey: (item: T) => K
) {
  const [selectedIds, setSelectedIds] = useState<Set<K>>(new Set())

  // 所有项的 ID
  const allIds = useMemo(() => items.map(getKey), [items, getKey])

  // 检查是否选中
  const isSelected = useCallback(
    (id: K) => selectedIds.has(id),
    [selectedIds]
  )

  // 切换选中状态
  const toggle = useCallback((id: K) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  // 选中单个
  const select = useCallback((id: K) => {
    setSelectedIds((prev) => {
      if (prev.has(id)) {return prev}
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }, [])

  // 取消选中单个
  const deselect = useCallback((id: K) => {
    setSelectedIds((prev) => {
      if (!prev.has(id)) {return prev}
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }, [])

  // 选中多个
  const selectMany = useCallback((ids: K[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => next.add(id))
      return next
    })
  }, [])

  // 取消选中多个
  const deselectMany = useCallback((ids: K[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => next.delete(id))
      return next
    })
  }, [])

  // 全选
  const selectAll = useCallback(() => {
    setSelectedIds(new Set(allIds))
  }, [allIds])

  // 清除选择
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  // 反选
  const invertSelection = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set<K>()
      allIds.forEach((id) => {
        if (!prev.has(id)) {
          next.add(id)
        }
      })
      return next
    })
  }, [allIds])

  // 范围选择（Shift + Click）
  const selectRange = useCallback(
    (fromId: K, toId: K) => {
      const fromIndex = allIds.indexOf(fromId)
      const toIndex = allIds.indexOf(toId)
      
      if (fromIndex === -1 || toIndex === -1) {return}

      const start = Math.min(fromIndex, toIndex)
      const end = Math.max(fromIndex, toIndex)
      const rangeIds = allIds.slice(start, end + 1)

      selectMany(rangeIds)
    },
    [allIds, selectMany]
  )

  // 是否全选
  const isAllSelected = useMemo(
    () => allIds.length > 0 && allIds.every((id) => selectedIds.has(id)),
    [allIds, selectedIds]
  )

  // 是否部分选中
  const isPartiallySelected = useMemo(
    () => selectedIds.size > 0 && !isAllSelected,
    [selectedIds.size, isAllSelected]
  )

  // 选中数量
  const selectedCount = selectedIds.size

  // 获取选中的项
  const selectedItems = useMemo(
    () => items.filter((item) => selectedIds.has(getKey(item))),
    [items, selectedIds, getKey]
  )

  // 获取选中的 ID 数组
  const selectedIdsArray = useMemo(
    () => Array.from(selectedIds),
    [selectedIds]
  )

  return {
    selectedIds,
    selectedIdsArray,
    selectedItems,
    selectedCount,
    isSelected,
    toggle,
    select,
    deselect,
    selectMany,
    deselectMany,
    selectAll,
    clearSelection,
    invertSelection,
    selectRange,
    isAllSelected,
    isPartiallySelected,
  }
}

/**
 * 批量操作工具栏属性
 */
export interface BatchActionsToolbarProps<K> {
  /** 选中数量 */
  selectedCount: number
  /** 是否全选 */
  isAllSelected: boolean
  /** 是否部分选中 */
  isPartiallySelected: boolean
  /** 全选回调 */
  onSelectAll: () => void
  /** 清除选择回调 */
  onClearSelection: () => void
  /** 批量操作 */
  actions: {
    label: string
    icon?: React.ReactNode
    onClick: (selectedIds: K[]) => void
    variant?: 'default' | 'destructive'
    disabled?: boolean
  }[]
  /** 选中的 ID */
  selectedIds: K[]
}

/**
 * 生成批量操作工具栏属性
 */
export function getBatchActionsToolbarProps<T, K extends string | number>(
  selection: ReturnType<typeof useBatchSelection<T, K>>,
  actions: BatchActionsToolbarProps<K>['actions']
): BatchActionsToolbarProps<K> {
  return {
    selectedCount: selection.selectedCount,
    isAllSelected: selection.isAllSelected,
    isPartiallySelected: selection.isPartiallySelected,
    onSelectAll: selection.selectAll,
    onClearSelection: selection.clearSelection,
    actions,
    selectedIds: selection.selectedIdsArray,
  }
}
